import { html, render } from 'lit-html'
import { register } from '@polleverywhere/hmr-custom-element'
import { defineElementSlot } from '@polleverywhere/element-helpers'
import './style.scss'

function template (header, content, progress) {
  return html`
    <header>
      ${header}
      <div name='progress-bar' ?show=${progress}></div>
    </header>
    <section>
      <div name='backdrop-wrapper'>
        <div name='backdrop'></div>
        <div name='content-wrapper'>
          ${content}
        </div>
      </div>
    </section>
  `
}

function renderTemplate (el) {
  if (!el.isConnected) return

  const progress = el.hasAttribute('progress')
  render(template(el.slots('header'), el.slots('content'), progress), el)
}

/**
 * Layout contains two slots for `<header>` and `<content>`. They are vertically
 * stacked in a single column with header on top of content. Header is typically
 * used to render navigation controls such as `Toolbar` into it.
 *
 * @element layout-content
 *
 * @attr {boolean} progress - Initiates the progress animation in the header
 * @attr {boolean} focus-header - Blocks and blurs `<content>` slot to prevent
 *    all user interactions, thus bringing sole focus to the `<header>`.
 *
 * @slot header - HTML element or document fragment to insert as header
 * @slot content - HTML element or document fragment to insert as content
 *
 * @cssprop --header-background-color - Set header background color
 * @cssprop --header-color - Set header foreground color
 * @cssprop --content-background-color - Set content background color
 * @cssprop --content-color - Set content foreground color
 * @cssprop --content-max-width - Set a max width around the content container
 * @cssprop --content-padding - Set padding around the content container. If
 *  overridden, directional padding vars will become ineffective unless they are
 *  included as part of the value, e.g.
 *  `--content-padding: var(--content-padding-top, 1rem);`
 * @cssprop --content-padding-top - Set top padding around content.
 * @cssprop --content-padding-right - Set right padding around content.
 * @cssprop --content-padding-bottom - Set bottom padding around content.
 * @cssprop --content-padding-left - Set left padding around content.
 * @cssprop --progress-color - Set progress bar color
 * @cssprop --progress-duration - Duration of progress bar animation
 * @cssprop --progress-end-delay - After progress bar completes, specify how
 *    long to wait before hiding it.
 */
export default class LayoutContent extends HTMLElement {
  static get tagName () {
    return 'layout-content'
  }

  static get observedAttributes () {
    return ['focus-header', 'progress']
  }

  connectedCallback () {
    defineElementSlot(this, 'header')
    defineElementSlot(this, 'content')

    renderTemplate(this)
  }

  attributeChangedCallback () {
    renderTemplate(this)
  }
}

register(module, LayoutContent.tagName, LayoutContent)
